import { useCallback, useEffect, useRef, useState } from 'react';

interface UseResizablePanelProps {
  isResizable: boolean;
  defaultSize: number;
  minSize: number;
  maxSize: number;
  side?: 'left' | 'right' | 'top' | 'bottom';
}

const TRANSITION_DURATION_MS = 300;

export const useResizablePanel = ({
  isResizable,
  defaultSize,
  minSize,
  maxSize,
  side = 'right',
}: UseResizablePanelProps) => {
  const isVertical = side === 'top' || side === 'bottom';
  const [size, setSize] = useState<string>(`${defaultSize}px`);
  const [isResizing, setIsResizing] = useState(false);
  const startPosRef = useRef<number>(0);
  const startSizeRef = useRef<number>(0);

  // Track transition state to delay enabling resizability during panel switches
  const prevDefaultSizeRef = useRef<number | null>(null);
  const prevIsResizableRef = useRef<boolean | null>(null);
  const isInitialMountRef = useRef(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isResizableEnabled, setIsResizableEnabled] = useState(false);

  // Handle initial mount and size changes - delay enabling resizability to allow smooth transitions
  useEffect(() => {
    const sizeChanged =
      prevDefaultSizeRef.current !== null &&
      prevDefaultSizeRef.current !== defaultSize;
    const isInitialMount = isInitialMountRef.current;

    const prevIsResizable = prevIsResizableRef.current;
    const resizableTurnedOn =
      prevIsResizable !== null &&
      prevIsResizable === false &&
      isResizable === true;

    // Update refs
    if (isInitialMount) {
      isInitialMountRef.current = false;
    }
    prevDefaultSizeRef.current = defaultSize;
    prevIsResizableRef.current = isResizable;

    // If resizable, always delay enabling to allow transition (on mount or size / resizable-flag change)
    if (isResizable && (isInitialMount || sizeChanged || resizableTurnedOn)) {
      setIsTransitioning(true);
      setIsResizableEnabled(false);
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setIsResizableEnabled(true);
      }, TRANSITION_DURATION_MS);
      return () => clearTimeout(timer);
    } else if (!isResizable) {
      // For non-resizable panels, disable immediately
      setIsResizableEnabled(false);
      setIsTransitioning(false);
    }
  }, [defaultSize, isResizable]);

  // Update size when defaultSize changes or when resizable becomes enabled
  // This ensures size is always in sync with defaultSize when resizable
  useEffect(() => {
    if (isResizableEnabled) {
      setSize(`${defaultSize}px`);
    }
  }, [defaultSize, isResizableEnabled]);

  const resizeHandleOnMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isResizableEnabled) return;
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      startPosRef.current = isVertical ? e.clientY : e.clientX;
      startSizeRef.current = parseInt(size, 10);
      document.body.style.cursor = isVertical ? 'row-resize' : 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [size, isResizableEnabled, isVertical],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !isResizableEnabled) return;

      let delta: number;
      if (isVertical) {
        // For bottom panels: dragging up (negative delta) makes panel smaller
        // For top panels: dragging down (positive delta) makes panel larger
        delta =
          side === 'bottom'
            ? startPosRef.current - e.clientY
            : e.clientY - startPosRef.current;
      } else {
        // For right-side panels: dragging left (negative delta) makes panel smaller
        // For left-side panels: dragging right (positive delta) makes panel larger
        delta =
          side === 'right'
            ? startPosRef.current - e.clientX
            : e.clientX - startPosRef.current;
      }
      const newSize = Math.max(
        minSize,
        Math.min(maxSize, startSizeRef.current + delta),
      );
      setSize(`${newSize}px`);
    },
    [isResizing, isResizableEnabled, minSize, maxSize, side, isVertical],
  );

  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }, [isResizing]);

  useEffect(() => {
    if (!isResizing || !isResizableEnabled) return;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, isResizableEnabled, handleMouseMove, handleMouseUp]);

  const resizeHandleOnDoubleClick = useCallback(() => {
    if (isResizableEnabled) {
      setSize(`${defaultSize}px`);
    }
  }, [isResizableEnabled, defaultSize]);

  return {
    size,
    width: isVertical ? undefined : size,
    height: isVertical ? size : undefined,
    isResizing,
    isResizableEnabled,
    isTransitioning,
    resizeHandleOnMouseDown,
    resizeHandleOnDoubleClick,
  };
};
